import { useState, useEffect } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Popover, PopoverPanel } from '@headlessui/react';
import { usePopper } from 'react-popper';
import { nanoid } from 'nanoid';
import Icon from '@/core/icon';
import { Markdown, useTranslation } from '@/helpers/translate';
import { useSpacePublicZustandStore } from '@/helpers/spacepublicprovider';
import { isClient } from '@/helpers/common.js';

const query = graphql`
  fragment formuleSpace_formules on SpaceFormule @relay(plural: true) {
    id
    spaceOnly
    day
    title
    slug
    description
    excl_tax
    minperson
    type
    index
    category
    groups {
      title
      extras
      order
      icon
    }
    extras {
      slug
      title
      icon
      description
      partnerRef
      rules {
        until
      }
    }
  }
`;

function Item({
  item, showDesc, showIcon, showTitle,
}) {
  const { t } = useTranslation();
  const title = (item?.title || item?.slug || '').replace('.', ':');
  return (
    <>
      {!showIcon || <Icon name={item.icon} className="hidden fill-current lg:block text-purple-5/50 w-7 h-7" />}
      {!showTitle || (<span className="leading-3">{t(title)}</span>)}
      {!showDesc || <Markdown>{item.description}</Markdown>}
    </>
  );
}

function FormuleItem({ item, isGroup, isLast }) {
  const [open, setOpen] = useState(false);
  const [refLabel, setRefLabel] = useState();
  const [refPopover, setRefPopover] = useState();
  const { styles, attributes } = usePopper(refLabel, refPopover, { placement: 'top' });
  return (
    <>
      <Popover as="fragment">
        <span
          ref={setRefLabel}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          <Item {...{ item }} showTitle />
        </span>
        {!open || (
        <PopoverPanel
          as="div"
          anchor="bottom start"
          className="z-10"
          static
          ref={setRefPopover}
          style={styles.popper}
          {...attributes.popper}
        >
          {isGroup ? (
            <div className="z-10 grid max-w-md px-6 py-5 my-3 space-y-3 text-sm bg-white rounded-lg grid-col-1 shadow-button">
              {item.dataExtras.reduce((acc, de) => {
                if (acc.length) {
                  acc.push(<Icon
                    name="plus"
                    className="w-3 h-3 my-4 fill-current text-dark-light"
                    key={`spacepublicateformuleitemicon${de.title}${de.slug}${de.description}`}
                  />);
                }
                acc.push(<Item {...{ item: de, showDesc: true, showIcon: true }} key={`spacepublicateformuleitemitem${de.title}${de.slug}${de.description}`} />);
                return acc;
              }, [])}
            </div>
          ) : <div className="z-10 flex justify-center max-w-md px-6 py-5 my-3 text-sm bg-white rounded-lg shadow-button">
            <Item {...{ item }} showDesc />
              </div>}
        </PopoverPanel>
        )}
      </Popover>
    </>
  );
}

function FormuleHead({ formule }) {
  const { t } = useTranslation();

  return (
    <div className="flex items-center justify-between">
      <span className="text-base font-medium text-black" dangerouslySetInnerHTML={{ __html: t(formule.title.replace('.', ':')) }} />
      <div className="flex items-center">
        <div className="text-base font-medium text-black-light md:mr-4">
          {formule.excl_tax}
          €
          <sup className="top-0 ml-1 text-sm text-dark">
            {t(formule.day ? 'space:per_day' : 'space:per_pers')}
          </sup>
        </div>
      </div>
    </div>
  );
}

function FormuleBody({ formule: srcformule }) {
  const formule = { ...srcformule };
  const { t } = useTranslation();
  const [reactKey1] = useState(nanoid());
  const [reactKey2] = useState(nanoid());

  let extrasgroups = [];
  if (formule.groups) {
    extrasgroups = formule.groups.reduce((acc, grp) => {
      let newAcc1 = [...acc];
      newAcc1 = newAcc1.concat(grp.extras.split(';'));
      return newAcc1;
    }, extrasgroups);
    formule.groups = formule.groups.map((grp) => {
      const newGrp = { ...grp };
      newGrp.dataExtras = [];
      grp.extras.split(';').map((e) => {
        const lookData = (formule?.extras || []).find((ee) => ee.slug === e);
        if (lookData) {
          newGrp.dataExtras.push(lookData);
        }
        return e;
      });
      return newGrp;
    });
  }
  return (
    <>
      <span className="leading-3">{t('extra:name.space')} - </span>
      {(() => {
        // Vérifier que formule.groups et formule.extras ne sont pas nulles ou undefined
        const groups = formule.groups || [];
        const extras = formule.extras ? formule.extras.filter((e) => !extrasgroups.includes(e.slug)) : [];

        // Fusionner les deux listes en une seule
        const combined = [
          ...groups.map((fgrp) => ({ ...fgrp, isGroup: true })),
          ...extras,
        ];

        // Rendre les éléments avec des séparateurs appropriés
        return combined.map((item, index) => (
          <span key={item.isGroup ? `${reactKey1}${item.extras}` : `${reactKey2}${item.slug}`}>
            <FormuleItem item={item} isGroup={item.isGroup} />
            {index < combined.length - 1 && (
              <span className="px-1">-</span>
            )}
          </span>
        ));
      })()}
    </>
  );
}
const getMetaFormules = (formule) => (
  <span className="hidden" itemProp="offers" itemScope itemType="http://schema.org/Offer">
    <meta itemProp="availability" content="PreOrder" />
    <meta itemProp="price" content={`${formule?.excl_tax}`} />
    <meta itemProp="priceCurrency" content="EUR" />
  </span>
);
function Formules({ formules }) {
  const { t } = useTranslation();
  useEffect(() => {
    if (formules) {
      useSpacePublicZustandStore.setState({ formules });
    }
  }, [formules]);

  let formuleSpaceOnly;

  if ((formules && !isClient()) || (formules && formules.length === 1)) {
    formuleSpaceOnly = formules.filter((f0) => f0).find((f) => f.spaceOnly);
    return getMetaFormules(formuleSpaceOnly);
  }
  if (!formules || formules.length <= 1) return null;
  formuleSpaceOnly = formules.filter((f0) => f0).find((f) => f.spaceOnly);

  // console.log('content by category formules');
  return (
    <>
      <section className="flex flex-wrap mx-auto mt-12 md:mt-16 md:flex-row text-dark">
        <h2 className="w-full pt-6 pb-2 text-xl font-medium text-black border-t border-white-dark2 md:pt-12 lg:pb-5">{t('space:type_booking')}</h2>
        <div className="grid w-full grid-cols-1 gap-2">
          {!formuleSpaceOnly || (
          <div className="text-base w-full border rounded-lg border-white-dark2 py-2 pl-4 pr-3">
            <FormuleHead formule={formuleSpaceOnly} />
          </div>
          )}
          {!formules || formules.map((formule) => (formule?.spaceOnly || (
            <div className="text-base w-full border rounded-lg border-white-dark2 py-3 pl-4 pr-3" key={formule.id}>
              <FormuleHead formule={formule} />
              <div className="pt-2">
                <FormuleBody formule={formule} />
              </div>
            </div>
          )))}
        </div>
      </section>
      <div>{getMetaFormules(formuleSpaceOnly)}</div>
    </>
  );
}

export { query, FormuleBody };

export default Formules;
